import { normalizeSailingCrewFinderSettings } from './moduleUtils';
import { safeSailingBoatLabel } from './sailingFleet';

export const CREW_FINDER_POST_TYPES = Object.freeze(['crew_needed', 'available_to_crew']);
export const CREW_FINDER_POST_STATUSES = Object.freeze(['open', 'filled', 'closed', 'cancelled']);
export const CREW_FINDER_BOAT_SOURCE_TYPES = Object.freeze(['club_boat', 'club_boat_booking', 'mooring_allocation', 'own_boat']);

export function normalizeCrewFinderPostType(value) {
  const type = String(value || '').trim();
  return CREW_FINDER_POST_TYPES.includes(type) ? type : 'crew_needed';
}

export function normalizeCrewFinderStatus(value) {
  const status = String(value || '').trim();
  return CREW_FINDER_POST_STATUSES.includes(status) ? status : 'open';
}

export function normalizeMemberTags(rawTags) {
  if (Array.isArray(rawTags)) {
    return rawTags.map((tag) => String(tag || '').trim()).filter(Boolean);
  }
  if (rawTags && typeof rawTags === 'object') {
    return Object.entries(rawTags)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([name]) => String(name || '').trim())
      .filter(Boolean);
  }
  return [];
}

export function canPostToCrewFinder({ modules = {}, memberTags = [], isAdmin = false, type = 'crew_needed' } = {}) {
  const settings = normalizeSailingCrewFinderSettings(modules?.sailing?.crewFinder || {});
  if (!settings.enabled) return false;
  if (type === 'crew_needed' && !settings.allowCrewNeededPosts) return false;
  if (type === 'available_to_crew' && !settings.allowAvailabilityPosts) return false;
  if (isAdmin) return true;
  if (settings.postingMode === 'all_members') return true;
  if (settings.postingMode !== 'tagged_members') return false;
  const memberTagSet = new Set(normalizeMemberTags(memberTags));
  return settings.postingTagNames.some((tagName) => memberTagSet.has(tagName));
}

export function safeCrewFinderBoatLabel({ boatSourceType = '', clubBoat = null, mooringAllocation = null, fallback = '' } = {}) {
  if (boatSourceType === 'club_boat') {
    return safeSailingBoatLabel(clubBoat || {}, { showName: true }) || String(fallback || 'Club boat').trim();
  }
  if (boatSourceType === 'club_boat_booking') {
    return String(fallback || 'Boat booking request').trim();
  }
  if (boatSourceType === 'mooring_allocation') {
    const vesselName = String(mooringAllocation?.vesselName || '').trim();
    const vesselType = String(mooringAllocation?.vesselType || '').trim();
    return [vesselName || 'Mooring vessel', vesselType && vesselType !== vesselName ? vesselType : ''].filter(Boolean).join(' · ');
  }
  if (boatSourceType === 'own_boat') {
    return String(fallback || 'Own/other boat').trim();
  }
  return String(fallback || '').trim();
}

export function crewFinderPostTypeLabel(type) {
  return normalizeCrewFinderPostType(type) === 'available_to_crew' ? 'Available crew' : 'Crew needed';
}

export function crewFinderStatusLabel(status) {
  const normalized = normalizeCrewFinderStatus(status);
  if (normalized === 'filled') return 'Crew found';
  if (normalized === 'cancelled') return 'Cancelled';
  if (normalized === 'closed') return 'Closed';
  return 'Open';
}
